import { Action, AnyAction, Store } from "redux"
import { Column, RowNode } from "ag-grid-community"
import { ExportCsvState, tGuestFormShares, tTransformForImportState } from "../reducers/types"
import { ModalSelectLanguage, tCopyToModalState } from "../components/modals/types"
import { ThunkAction, ThunkDispatch } from "redux-thunk"
import { getFlagEnabled, getFlagValue } from "../getFlagValue"
import { iCompanyFormSchema, iTimelineEntryVersionSummary, tProject } from "../cached-data/types"
import {
    iCompanyFormStore,
    iCostCode,
    iEmployeeWorkShift,
    iEquipment,
    iPicklist,
    iTimekeepingStatus,
    iTkModifier,
    iWorkShift,
    tCachedResourceName,
    tEntityState,
    tPaginationState,
} from "../cached-data/types"
import { iMutableSourceDataState, tNetworkStatusState, tResourceObject, tSourceData } from "../dashboard-data/types"
import { tFilterState, tStartOfWeekIndex } from "../filters/types"

import { CicoQRCodeLightboxState } from "../components/modals/reducers"
import { CompareStoreType } from "../forms/BulkReviewForm/types"
import { ImportToFieldFormModalParamsType } from "../forms/FieldFormsForm/types"
import { tContext } from "../components/custom-dashboards/types"
import { tSchemaVariantState } from "../reducers/schema-variants"
import { tSocketNotificationState } from "../websockets/reducers"
import { tByProjectCacheState } from "../reducers/by-project-cache"
import { tWeatherState } from "../reducers/weather"
import { NotificationState } from "../components/toastNotifications/reducers"
import { ExpressionObject as ExportTokenExpressionObject } from "../forms/CSVExportForm/tokenizer"
import { TKCostCodeModifier, TKEquipmentModifier } from "./ts-utils"
import { TkSummaryTimecard, WorkRuleSetRule } from "@rhumbix/rmbx_work_rules"

declare global {
    interface Window {
        amplitude_key: string
        capn_version: string
        Intercom: any
        intercom_api_key: string
        ld_client_id: string
        ld_flag_bootstrap: Record<string, unknown> | null
        rmbx: {
            getFlagEnabled: typeof getFlagEnabled
            getFlagValue: typeof getFlagValue
        }
        msCrypto: Crypto
        rmbx_env: string
        mapbox_public_key: string
    }
}

export interface iActionData extends Action {
    data: Record<string, any>
}

export type tJSONValue = null | string | number | boolean | iJSONObject | iJSONArray

export interface iJSONObject {
    [x: string]: tJSONValue
}

// API v4 errors are mapped to a field, e.g. /project or /modifier_active/non_field_errors
// with one or more errors attached to each field. We also use other keys (like `serverErrors`)
// to indicate row-level errors.
export type GridFieldErrors = {
    errors?: Record<string, string[]>
}

type iJSONArray = Array<tJSONValue>

interface iHttpResponse {
    headers: Array<string>
    ok: boolean
    redirected: boolean
    status: number
    statusText: string
    url: string
    body: string
    json: Promise<string>
    text: Promise<string>
    // TODO: subclass this as an error type to avoid having to coerce errors
    // then provide a property that tells if it's a 40x or a 50x and possibly logs the 500s automatically
    error?: string
    detail?: string
}

export type tHttpResponse = Readonly<iHttpResponse>

export type tHttpResponseFields = keyof tHttpResponse

/**
 * All resource names that have URLs, including both cached and non-cached resources.
 * Extends tCachedResourceName with additional resource names that are not cached.
 */
export type tUrlForResource = { [K in tCachedResourceName]: string } & {
    absences: string
    accountSettings: string
    apiIntegration: string
    autodeskToken: string
    companyAnalyticsDashboards: string
    companyFormSchemasForDashboard: string
    companyHolidays: string
    companyPTOBuckets: string
    companyTextFieldOptions: string
    dailySummaryPreferences: string
    dummy: string
    employeeCertifications: string
    employeeEntries: string
    employeePTORequests: string
    employeeLicenses: string
    employeeSchemasForDashboard: string
    employeeUnions: string
    equipmentRates: string
    ewsStartStopTimes: string
    groupMembers: string
    groupPtoBuckets: string
    equipmentCategories: string
    integrationActivity: string
    materialGroups: string
    materialPartNumbers: string
    materialRates: string
    modifyProduction: string
    projectCities: string
    projectClientNames: string
    projectCountries: string
    projectShares: string
    projectStates: string
    projectZipCodes: string
    ptoBuckets: string
    quantities: string
    schemaActions: string
    timeCards: string
    timekeepingEntries: string
    timekeepingModifiersForDashboard: string
    timelineEntries: string
    tkExportFormats: string
    tkExportRuns: string
    tkExportSubmissions: string
}

export type tHttpVerb = "CONNECT" | "DELETE" | "GET" | "HEAD" | "OPTIONS" | "PATCH" | "POST" | "PUT" | "TRACE"

export type tResourceName = keyof tUrlForResource

export type tValueFormatter = { (args: any): string }

export interface iReferenceableValueFormatters {
    valueFormatter?: tValueFormatter
    prefixFormatter?: tValueFormatter
    titleFormatter?: tValueFormatter
    primarySubtitleFormatter?: tValueFormatter
    secondarySubtitleFormatter?: tValueFormatter
    tertiarySubtitleFormatter?: tValueFormatter
    titleAndStatusFormatter?: tValueFormatter
    cicoValueFormatter?: tValueFormatter
}

export type tReferenceablesToValueFormatters = { [key in tResourceName]?: iReferenceableValueFormatters }
export type tReferenceableInactiveChecks = { [key in tResourceName]?: (item: Record<string, any>) => boolean }

// auth state from API v3
export type tUserRole =
    | "WORKER"
    | "FOREMAN"
    | "ADMIN"
    | "PM"
    | "OFFICE_STAFF"
    | "PAYROLL_ADMIN"
    | "GUEST"
    | "GUEST_SIGNATURE"

export type tCompanyRole = "OWNER" | "GENERAL_CONTRACTOR" | "SUBCONTRACTOR"

export type tFeatureFlagKeys =
    | "admin.api_integration"
    | "admin.bim360"
    | "admin.mass_text"
    | "admin.quantity_edit"
    | "budget_cost_codes"
    | "cico"
    | "cmic"
    | "cost_code_controls"
    | "cost_code_level_modifiers"
    | "custom_dashboards_beta"
    | "custom_dashboards_demo"
    | "custom_form"
    | "daily_foreman_only_signature"
    | "daily_report"
    | "direct_integrations"
    | "disable_mobile_shift_setup_cost_code_repopulate"
    | "disable_web_signature_flow"
    | "disable_work_shift_start_stop_times"
    | "duplicate_shift_extras"
    | "email_settings"
    | "enterprise_sso_configuration"
    | "equipment"
    | "force_new_field_names"
    | "foreman_access_all_project_forms"
    | "gc_company"
    | "gc_optional_workflow"
    | "group_restricted_data"
    | "groups"
    | "hide_daily_view"
    | "hide_notes"
    | "impersonate"
    | "large_project_count_optimizations"
    | "maps"
    | "materials"
    | "materials_requisition"
    | "mobile_production"
    | "pco_number_dashboard"
    | "password_timeout"
    | "payroll_approvals_no_ap_ex"
    | "pdf_printing"
    | "pricing"
    | "rate_tables"
    | "read_only_cost_codes"
    | "read_only_employees"
    | "report.payroll"
    | "shift_extras"
    | "shifts_and_breaks"
    | "show_attached_pdfs_inline"
    | "signature_webview_mobile"
    | "sso_login"
    | "surespan_verification"
    | "text_for_signature"
    | "timekeeping"
    | "timekeeping_comments"
    | "tm_invite_campaign"
    | "tm_pilot_workflow"
    | "web_production"
    | "work_rules"

export type tFeatures = { [key in tFeatureFlagKeys]: boolean }

export type tCompany = {
    id: number
    start_of_week: DayOfWeek
    start_of_week_index: tStartOfWeekIndex // calculated by get_current_user()
    company_name: string
    registration_date: string
    company_description: string | null
    company_website: string | null
    csm: string | null
    is_active: boolean
    various_info: iJSONObject | null
    address: string | null
    logo?: string
    role: {
        name: string
        value: string
    }
}

export type tCurrentUserEmployee = {
    company: tCompany //api v3 returns whole company object
    allowed_tk_statuses?: string[]
    id: number
    email: string
    phone: string | null
    company_supplied_id: string
    first_name: string
    last_name: string
    classification: string
    trade: string
    user_role: tUserRole
    fullsize: string | null
    thumbnail: string | null
    user_id: number
    employee_id: number
    is_active: boolean
    is_rhumbix_user: boolean
}

export type SignatureOptions = {
    require_signature?: boolean
    signature_period?: SignaturePeriod
    signature_roles?: string // TODO remove this
    signature_pad_type?: SignaturePadType
    prompt_eod_questions?: boolean
    display_start_stop?: boolean
    display_shift_extras?: boolean
    display_modifiers?: boolean
}

export type tCompanyProjectOptions = SignatureOptions & {
    enable_pricing?: boolean
    employee_pricing_type?: string
    equipment_pricing?: boolean
    material_pricing?: boolean
    timekeeping_increment?: number
}

export interface iCurrentUser {
    id: number
    // This is using the API v3 serializer, not the API v4 serializer we use
    // for referenceables
    employee: tCurrentUserEmployee
    employee_id: number
    user_id: number
    first_name: string
    last_name: string
    user_role: tUserRole
    email: string
    phone: string // We serialize None to "None" here ¯\_(ツ)_/¯
    fullsize: string | null
    thumbnail: string | null
    company_supplied_id: string
    date_joined: string
    company: string
    company_id: number
    company_role: string
    company_options: tCompanyProjectOptions & {
        id: number
        lock_end_date: string | null
        timekeeping_increment?: number
        tkcomment_max_length?: number
    }
    list_views: Array<any>
    features: tFeatures
    is_staff: boolean
    company_tk_modifiers?: Array<iTkModifier>
    employee_attribute_picklists?: Array<iPicklist>
    employee_start_of_week?: DayOfWeek
    text_field_options: Array<tCompanyTextFieldOption>
}

export type tTransformOption = {
    created_on: string
    from_name: string
    from_schemas: number[]
    id: number
    name: string
    single_item_transform: boolean
    template: string
    to_name: string
    to_schema: number
    is_bundle?: boolean
}

export type tCustomFormTransform = {
    transform_id: number
    project_id: number
    schema_id: number
    store_ids: number[]
}

export type tFileAttachment = {
    name?: string
    type?: string
    url: string
    description?: string
}

export type tSchemaPermission = {
    company_role: "GC" | "SUB" | "GUEST"
    schema_id: number
    user_role: tUserRole
    status: number
    object_permissions: any
    field_permissions: any
}

export type GuestUser = {
    id: number
    email: string
    company_name: string
    job_title: string
    first_name: string
    last_name: string
}

export type GuestFormShare = {
    id: number
    permissions: "REQUEST_SIGNATURE" | "VIEW_ONLY"
    guest: GuestUser
    last_updated: string
    form_history_id: number
    view_count: number
    access_expires_on: string
    created_on: string
    form: number
    created_by: number
}

export type ModalCustomFormGuestSave = {
    open: boolean
    currentStatus: string
    nextStatuses: string[]
    storeId: string
    validatedData: any
    errors: string[]
    guestEmail: string
    signedKey: string
    guestLastName: string
    guestExpiration: number
}

export type ModalGuestInfo = {
    guestEmail: string
    open: boolean
    signedKey: string
}

export type ModalGuestInfoError = {
    open: boolean
    error: string
}

export type ModalCreateSavedFilter = {
    open: boolean
    filterState: tFilterState
    page: string
    message: string
}

export type ModalDeleteSavedFilter = {
    open: boolean
    id: number
    page: string
    message: string
}

export type ModalAddRowsToWeeklyTKModal = {
    open: boolean
    resource: tResourceName
    resourceDisplayName: string
    context: tContext
    colDefs: Column[]
}

export type ModalAddCohortEmployeesModal = {
    open: boolean
    cohortId: number
    context: tContext
    colDefs: Column[]
}

export type ModalAddPicklistItem = {
    open: boolean
    context: tContext
    itemName: string
    picklistId: string
    picklistName: string
    costCodeId: number | iCostCode
    row: RowNode
}

export type ModalWeeklyAddWorkShift = {
    open: boolean
    context: tContext
}

export type ModalAddEditWorkShift = ModalWeeklyAddWorkShift & {
    workShift: iWorkShift | number
    sourceData: tSourceData
    row: RowNode
}

export type ModalImportToFieldForm = {
    importToFieldFormModalOpen: boolean
    importToFieldFormModalParams?: ImportToFieldFormModalParamsType
}

export type ModalLeaveFieldFormState = {
    isLeaveFieldFormModalOpen: boolean
}

export type ModalSSOConfigurationConfirmation = {
    open: boolean
    errorMessage: string
}

export type modalTKSignTimecard = {
    open: boolean
}

type ModalExportTokenDocumentation = {
    open: boolean
    token: ExportTokenExpressionObject
}

export type ModalChooseLaborRates = {
    open: boolean
    selectedRows: tResourceObject[]
    projectId: number | null
}

export type ModalsState = {
    modalAddRowsToWeeklyTKModal: ModalAddRowsToWeeklyTKModal
    modalAddCohortEmployeesModal: ModalAddCohortEmployeesModal
    modalAddEditWorkShift: ModalAddEditWorkShift
    modalAddPicklistItem: ModalAddPicklistItem
    modalCustomFormGuestSave: ModalCustomFormGuestSave
    modalGuestInfo: ModalGuestInfo
    modalGuestInfoError: ModalGuestInfoError
    modalCopyTo: tCopyToModalState
    modalCreateSavedFilter: ModalCreateSavedFilter
    modalDeleteSavedFilter: ModalDeleteSavedFilter
    modalImportToFieldForm: ModalImportToFieldForm
    modalSSOConfigurationConfirmation: ModalSSOConfigurationConfirmation
    modalTKSignTimecard: modalTKSignTimecard
    modalWeeklyAddWorkShift: ModalWeeklyAddWorkShift
    modalLeaveFieldForm: ModalLeaveFieldFormState
    modalQRCode: CicoQRCodeLightboxState
    modalLanguageSelector: ModalSelectLanguage
    modalExportTokenDocumentation: ModalExportTokenDocumentation
    modalChooseLaborRates: ModalChooseLaborRates
}

export type FancySearchTerm = {
    term: string[] | string
    category: string
    doFilter: boolean
}

export type SearchBarState = {
    cicoVisible: boolean
    searchTerm: string
    fancySearchTerm: FancySearchTerm
    extraTimelineVisible: boolean
    isFancySearchVisible: boolean
    nonExceptionRowsVisible: boolean
    placeholdersVisible: boolean
    showEmployeeMode: string
}

export type CicoDiffExceptionSettings = {
    enabled: boolean
    value: string
    units: "hours" | "percent"
    flagAnyEdits: boolean
    flagOthersEdits: boolean
    ignoreNoCico: boolean
}

export type CicoDiffException = {
    cicoDiffException: CicoDiffExceptionSettings
}

export type HoursExceptionSettings = {
    enabled?: boolean
    minHours: string
    maxHours: string
    minSTHours: number
    minSTHoursEnabled: boolean
    weekly: boolean
}

export type HoursException = {
    hoursException: HoursExceptionSettings
}

export type ShiftsBreaksExceptionSettings = {
    enabled: boolean
    missingShift: boolean
    missingMeal: boolean
    missingBreak: boolean
    minMealDurationEnabled: boolean
    minMealDuration: number
    maxTimeBeforeMealEnabled: boolean
    maxTimeBeforeMeal: number
}
export type ShiftsBreaksException = {
    shiftsBreaksException: ShiftsBreaksExceptionSettings
}

export type WorkRulesException = {
    workRulesException: WorkRulesExceptionSettings
}
export type WorkRulesExceptionSettings = {
    enabled: boolean
    workRuleOverride: WorkRuleSetRule[]
}

export type AnyException = CicoDiffException | HoursException | ShiftsBreaksException | WorkRulesException
export type AllExceptions = CicoDiffException & HoursException & ShiftsBreaksException & WorkRulesException
export type ExceptionsState = AllExceptions & {
    filterExceptions: boolean
    tkSummary: any | null
}

export type tSelectedParty = {
    permissions: "VIEW_ONLY" | "REQUEST_SIGNATURE"
    email_address: string
}

export enum ApprovalStatus {
    NOT_APPROVED = "PENDING",
    PAYROLL_VERIFIED = "APPROVED",
    SUPERVISOR_APPROVED = "SUPERVISOR_APPROVED",
    EXPORTED = "EXPORTED",
    SYNCED = "SYNCED",
}

// Common fields between Work Components, Absences, and Shift Extra Stores
export type BaseTkEntry = {
    date: string
    created_on: string
    employee: number
    employee_work_shift_id: number
    foreman: number
    id: number
    project: number
    shift_end_time: string
    shift_start_time: string
    status: string | iTimekeepingStatus
    work_shift_id: number
    approver_id?: number // Remove approver_id with WA-9095-tk-pdf-approval-statuses flag
    // Weekly TK annotation that shows first employee who updated the TK Entry to an "approval status"
    approvers_by_status?: {
        [key: string]: number
    }
    // Work rule annotations populated by ``memoizedAnnotateRowDataForWorkRules``. Present only
    // on rows that have flowed through the Weekly View work-rules selector.
    workRuleErrorMessages?: unknown[]
    workRuleWarningMessages?: unknown[]
    workRuleErrors?: unknown[]
    workRuleWarnings?: unknown[]
}

export type Absence = BaseTkEntry & {
    absence_type: number
    code: string
    type: string
}

export type EmployeeFormStore = BaseTkEntry & {
    display_modifiers?: boolean
    display_shift_extras?: boolean
    display_start_stop?: boolean
    ews_id: number
    prompt_eod_questions?: boolean
    require_signature?: boolean
    schema: number
    signature_pad_type?: SignaturePadType
    signature_period?: SignaturePeriod
    store: iJSONObject
    signature?: string
}

export type SignaturePeriod = "DAILY" | "WEEKLY" | "UNKNOWN"

export type SignaturePadType = "HYBRID" | "DRAW" | "TYPE"

export type SignatureSettings = SignatureOptions & {
    require_signature: boolean
    signature_period: SignaturePeriod
}

export type EODQuestionResponses = {
    injured: boolean | null
    breaks: boolean | null
    stretch: boolean | null
}

export type TimekeepingEntry = BaseTkEntry &
    SignatureSettings &
    EODQuestionResponses & {
        adjusted_minutes_dt: number
        adjusted_minutes_ot: number
        adjusted_minutes_st: number
        rate_match_dt?: number
        rate_match_ot?: number
        rate_match_st?: number
        comment: string | null
        cost_code: number | { id: number; code: string; project: tProject }
        modifier_active?: {
            [key: string]: number | TKCostCodeModifier | TKEquipmentModifier
        }
        signature: string
    }

/**
 * Row data type for timekeeping entries in the Weekly TK Detail Modal.
 * Extends TimekeepingEntry (API data) with client-side fields used during create/edit.
 */
export type TimekeepingEntryModalRowData = TimekeepingEntry & {
    gridId: number
    /*
     * The labor_rate_*_override fields have tri-state semantics:
     * - number: user selected a specific labor rate via the Choose Rates workflow
     * - null: user chose to reset the assigned labor rate via Reprice
     * - undefined: user has not yet gone through any clientside workflow (default)
     * These fields are write-only -- only used during create/edit actions.
     * When reading TK data, rate override data must be looked up from saved LaborRateMatch references.
     */
    labor_rate_st_override: number | null | undefined
    labor_rate_ot_override: number | null | undefined
    labor_rate_dt_override: number | null | undefined
}

export type EmployeeWorkShiftStartStopTime = Omit<BaseTkEntry, "project"> & {
    duration_minutes: number
    is_break: boolean
    start_stop_type: string
    company_start_stop_type: number
    start_time: string
    stop_time: string
}

export type tRequestTimekeepingSignatures = {
    signatureUrl: string
}

export const FETCH_COMPANY_SSO_CONFIGURATION_SUCCEEDED = "FETCH_COMPANY_SSO_CONFIGURATION_SUCCEEDED"
export const FETCH_COMPANY_SSO_CONFIGURATION_FAILED = "FETCH_COMPANY_SSO_CONFIGURATION_FAILED"
export const CREATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED = "CREATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED"
export const CREATE_COMPANY_SSO_CONFIGURATION_FAILED = "CREATE_COMPANY_SSO_CONFIGURATION_FAILED"
export const UPDATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED = "UPDATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED"
export const UPDATE_COMPANY_SSO_CONFIGURATION_FAILED = "UPDATE_COMPANY_SSO_CONFIGURATION_FAILED"

export interface iFetchCompanySSOConfigurationSucceeded extends Action {
    type: typeof FETCH_COMPANY_SSO_CONFIGURATION_SUCCEEDED
    data: any
}

export interface iFetchCompanySSOConfigurationFailed extends Action {
    type: typeof FETCH_COMPANY_SSO_CONFIGURATION_FAILED
}

export interface iCreateCompanySSOConfigurationSucceeded extends Action {
    type: typeof CREATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED
}

export interface iCreateCompanySSOConfigurationFailed extends Action {
    type: typeof CREATE_COMPANY_SSO_CONFIGURATION_FAILED
}

export interface iUpdateCompanySSOConfigurationSucceeded extends Action {
    type: typeof UPDATE_COMPANY_SSO_CONFIGURATION_SUCCEEDED
}

export interface iUpdateCompanySSOConfigurationFailed extends Action {
    type: typeof UPDATE_COMPANY_SSO_CONFIGURATION_FAILED
}

export type tCompanyTextFieldOption = {
    model: string
    name: string
    label: string
    read_only_in_app: boolean
    included_in_details: boolean
}

export type tCompanySSOConfigurationActions =
    | iFetchCompanySSOConfigurationSucceeded
    | iFetchCompanySSOConfigurationFailed
    | iCreateCompanySSOConfigurationSucceeded
    | iCreateCompanySSOConfigurationFailed
    | iUpdateCompanySSOConfigurationSucceeded
    | iUpdateCompanySSOConfigurationFailed

export type PageNumberPaginatedResponse<T> = {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

// TODO: Fill this in with slices of the state as we go.
export type ReduxState = {
    activeSavedFilter: any // TODO: Type this
    auth: {
        email: string
        errorMessage: string
        initialized: boolean
        isLoading: boolean
        isLoginCheckPassed: boolean
        isOidcSsoEnabled: boolean
        // can be removed with RN-1757-cico-worker-experience
        unverifiedUserRole: string
        token: string | null
        guestEmail?: string
        guestFirstName?: string
        guestLastName?: string
        onAssignedExceptionsList: false
        guestPermissions?: Record<string, any>
        guestCompanyLogoUrl?: string
        guestExpiration?: number
        guestFormId?: number
    }
    byProjectCache: tByProjectCacheState
    company: {
        logo: string
    }
    companyFormSchema: {
        all: iCompanyFormSchema[]
        single: iCompanyFormSchema
    }
    companyFormStore: {
        all: iCompanyFormStore[]
        compare: CompareStoreType
    }
    customForm: {
        customFormData: iCompanyFormStore
        project: tProject
        downloadUrl?: string
        maxHistory: iCompanyFormStore | null
        editingActive?: boolean
    }
    companySSOConfiguration: Record<string, any>[]
    current_user: iCurrentUser
    employeeWorkShifts: iEmployeeWorkShift[]
    entities: tEntityState
    exceptions: ExceptionsState
    exportCSV: ExportCsvState
    featureFlags: {
        features: Record<string, boolean>
    }
    filters: tFilterState
    groupBy: Record<string, string>
    guestFormShares: tGuestFormShares
    modals: ModalsState
    networkStatus: tNetworkStatusState
    pagination: tPaginationState
    pendingFilters: tFilterState
    projectEquipment: { equipment: iEquipment[] }
    requestTimekeepingSignatures: tRequestTimekeepingSignatures
    savedFilterSets: Record<string, any>[]
    schemaPermissions: { schemaPermissions: Array<tSchemaPermission> }
    schemaVariants: tSchemaVariantState
    searchBar: SearchBarState
    socketNotification: tSocketNotificationState
    sourceData: iMutableSourceDataState
    transformsForImport: tTransformForImportState
    weather: tWeatherState
    toastNotification: NotificationState
    currentProject: tProject | null
    clipboard: string
}

export type AppDispatch = ThunkDispatch<ReduxState, null, AnyAction>
export type AppStore = Store<ReduxState, AnyAction> & { dispatch: AppDispatch }

/**
 * These Thunk types aren't perfect, but hopefully they help out in the short-term.
 * We can eventually update the type of Action<any> if we type out all of our Redux actions.
 * Haven't yet figured out how to have TS complain if the Thunk type is used for an async function.
 *
 * Specify a return type if your thunk returns a value.
 * Use AsyncThunk with a return type if your dispatched thunk returns a Promise.
 *
 * Usage:
 *
 *     const foo = (arg1, ...): Thunk => (dispatch [, getState]) => { ... }
 *     dispatch(foo(arg1, arg2))
 *
 *     const bar = (): Thunk<string> => (dispatch [, getState]) => { return "whoda thunk?" }
 *     const ret = dispatch(bar())
 *
 *     const baz = (): AsyncThunk<number> => async (dispatch [, getState]) => { return 42 }
 *     const ret = await dispatch(baz())
 *     // or, alternatively:
 *     dispatch(baz()).then((ret) => { ... })
 */
export type Thunk<T = void> = ThunkAction<T, ReduxState, null, AnyAction>
export type AsyncThunk<T = void> = ThunkAction<Promise<T>, ReduxState, null, AnyAction>

export type MixedToggleState = "on" | "off" | "mixed"

export type DayOfWeek = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY"

export type CicoEventType = "CLOCK_IN" | "CLOCK_OUT" | "START_BREAK" | "END_BREAK" | "START_MEAL" | "END_MEAL"
// | "CHANGE_TASK"

export type MetadataCapture = "REQUIRED" | "CAPTURED" | "DISABLED"
export type DistanceUnit = "MI" | "KM"
export type GeofenceDrawMode = "polygon" | "radius"

export type Device = {
    brand: string
    device_id: string
    name: string
    os_name: string
    os_version: string
}

export enum FormStyle {
    LEGACY = "LEGACY",
    COMPACTV1 = "COMPACTV1",
}

export enum Language {
    ENGLISH = "en",
    SPANISH = "es",
}

export const LanguageStrings: Record<Language, string> = {
    [Language.ENGLISH]: "English",
    [Language.SPANISH]: "Español",
}

export type StartStopType = "Break" | "Meal" | "Shift"

export type CustomRateType = {
    id: number
    name: string
    label: string
    company_id: number
    show_on_labor_rate: boolean
    show_on_material_rate: boolean
    show_on_equipment_rate: boolean
}

export type RateType = "laborRates" | "equipmentRates" | "materialRates"

export type RateTable = {
    company_id: number
    created_by_id: number
    created_on: string
    custom_rate_types: CustomRateType[]
    description: string
    equipment_rates: NestedEquipmentRate[]
    id: number
    labor_rates: NestedLaborRate[]
    last_updated: string
    material_rates: NestedMaterialRate[]
    name: string
    version: number
    start_date: string | null
    end_date: string | null
}

type RateCustomFieldType = "employee" | "material" | "equipment" | "cost_code" | "project"

type BaseRate = {
    base_rate: number
    custom_fields: Partial<Record<RateCustomFieldType, Record<string, string[]>>> | null
    custom_rates: Record<string, number> | null
    id: number
    name: string
    row_order: number
}

/**
 * Represents a labor rate as nested within a RateTable response.
 * This is the raw labor rate data without the parent rate table reference.
 */
export type NestedLaborRate = BaseRate & {
    certifications: number[]
    classifications: string[]
    cohorts: number[]
    equipment_modifiers: number[]
    licenses: number[]
    project_client_names?: string[]
    time_type: TkTimeType
    trades: string[]
    unions: number[]
    // Dynamic custom modifier fields in the format: custom_modifier_{picklist_name}
    [key: `custom_modifier_${string}`]: number[] | undefined
}

export type LaborRateMatch = {
    id: number
    labor_rate: number | null
    labor_rate_override: number | null
}

/**
 * Represents an equipment rate as nested within a RateTable response.
 * This is the raw equipment rate data without the parent rate table reference.
 */
export type NestedEquipmentRate = BaseRate & {
    categories: string[]
    equipment: number[]
    idle_rate?: number | null
    overtime_rate?: number | null
    statuses?: string[] | null // RENTED or OWNED
}

/**
 * Represents a material rate as nested within a RateTable response.
 * This is the raw material rate data without the parent rate table reference.
 */
export type NestedMaterialRate = BaseRate & {
    groups: string[]
    part_numbers: string[]
    materials: number[]
}

export type TkCellData = {
    ST: number
    DT: number
    OT: number
    cicoByProject: Record<number, number>
    cicoEntries: iTimelineEntryVersionSummary[]
    cicoTime: number
    clocked_in_duration: number
    consecutive_days: number
    meal_duration: number
    break_duration: number
    version_count: number
    isTotalCell?: boolean
    item_count: number
    edited_by_other: number
    shift_date: string
    startStopTypes: Set<string>
    startStopTimes: EmployeeWorkShiftStartStopTime[]
    weekly_hours: number
    workshiftId: Set<number>
}

export type TkRowData = TkCellData & {
    byDate: Record<string, TkCellData>
}

export type TkSummary = {
    // The key is the employee ID
    [key: string]: TkSummaryTimecard[]
}

export type TkSummaryShiftsAndBreaks = {
    company_start_stop_type?: number | { name: string; id: number; is_break: boolean; is_active: boolean }
    start_stop_type: string
    duration_minutes: number
    start_time: string
    stop_time: string
    is_break: boolean
    shift_date: string
}

export type TkTimeType = "ST" | "OT" | "DT" | "TT"
export type BasicTkTimeType = Exclude<TkTimeType, "TT">
